(() => {
  if (window.OlvaxinVariantSelector?.lifecycleVersion) {
    window.OlvaxinVariantSelector.init(document);
    return;
  }

  const instances = new WeakMap();
  const rootSelector = ".shop-product-section, .featured-product-section, [data-section-id]";

  class VariantSelector {
    constructor(container) {
      this.container = container;
      this.productCards = Array.from(container.querySelectorAll(".variant-product-card"));
      this.selectedVariant = null;
      this.callbacks = [];
      this.controller = new AbortController();
      this.init();
    }

    init() {
      this.productCards.forEach((card, index) => {
        card.addEventListener("click", () => this.selectVariant(index), { signal: this.controller.signal });
      });
      const selectedCard = this.container.querySelector(".variant-product-card.selected");
      if (selectedCard) this.selectedVariant = this.productCards.indexOf(selectedCard);
      else if (this.productCards.length) this.selectVariant(0);
    }

    selectVariant(index) {
      const card = this.productCards[index];
      if (!card) return;
      this.productCards.forEach((item) => {
        const selected = item === card;
        item.classList.toggle("selected", selected);
        item.setAttribute("aria-pressed", String(selected));
      });
      this.selectedVariant = index;
      const variant = this.getVariantData(index);
      this.callbacks.forEach((callback) => callback(variant, index));
      this.container.dispatchEvent(
        new CustomEvent("variantSelected", {
          bubbles: true,
          detail: { variant, index, element: card, scope: sectionRoot(this.container) },
        }),
      );
    }

    getVariantData(index) {
      const card = this.productCards[index];
      if (!card) return null;
      return {
        title: card.querySelector(".variant-product-title")?.textContent?.trim() || "",
        description: card.querySelector(".variant-product-description")?.textContent?.trim() || "",
        originalPrice: card.querySelector(".variant-original-price")?.textContent?.trim() || "",
        currentPrice: card.querySelector(".variant-current-price")?.textContent?.trim() || "",
        hasBadge: Boolean(card.querySelector(".variant-best-value-badge")),
        badgeText: card.querySelector(".variant-best-value-badge")?.textContent?.trim() || "",
        variantId: card.dataset.variantId || null,
        productId: card.dataset.productId || null,
        variantPrice: card.dataset.variantPrice || null,
        variantComparePrice: card.dataset.variantComparePrice || null,
        variantAvailable: card.dataset.variantAvailable === "true",
      };
    }

    getSelectedVariant() {
      return this.selectedVariant === null ? null : this.getVariantData(this.selectedVariant);
    }

    onVariantSelect(callback) {
      this.callbacks.push(callback);
    }

    updateVariantColors(colors) {
      Object.entries(colors || {}).forEach(([property, value]) => {
        if (value) this.container.style.setProperty(`--variant-${property.replace(/_/g, "-")}`, value);
      });
    }

    dispose() {
      this.controller.abort();
      instances.delete(this.container);
      delete this.container.variantSelector;
    }
  }

  function sectionRoot(container) {
    return container.closest(rootSelector) || container;
  }

  function applyVariant(container, variant) {
    if (!variant?.variantId) return;
    const scope = sectionRoot(container);
    const productForm =
      (typeof OLVAXIN_GetProductForm === "function" && OLVAXIN_GetProductForm(scope)) ||
      scope.querySelector('form[action*="/cart/add"]');
    const variantInput = productForm?.querySelector('input[name="id"], select[name="id"]');
    if (variantInput) {
      variantInput.value = variant.variantId;
      variantInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    scope.querySelectorAll(".shop-product-price, .product-price, [data-price]").forEach((element) => {
      if (variant.currentPrice) element.textContent = variant.currentPrice;
    });
    const addToCartText = scope.querySelector(
      ".product-form__submit .button__text, .shop-add-to-cart-button .button-text, [data-add-to-cart-text]",
    );
    if (addToCartText) addToCartText.textContent = variant.currentPrice ? `${(window.olvaxinStrings?.addToCartLower || "Add to cart")} - ${variant.currentPrice}` : (window.olvaxinStrings?.addToCartLower || "Add to cart");

    if (window.history?.replaceState && location.pathname.includes("/products/")) {
      const url = new URL(window.location);
      url.searchParams.set("variant", variant.variantId);
      window.history.replaceState({}, "", url);
    }
    const eventDetail = { ...variant, scope, source: "olvaxin-variant-selector" };
    document.dispatchEvent(new CustomEvent("variantChanged", { detail: eventDetail }));
    window.dispatchEvent(new CustomEvent("variantChanged", { detail: eventDetail }));
  }

  function initContainer(container) {
    if (instances.has(container)) return instances.get(container);
    const selector = new VariantSelector(container);
    selector.onVariantSelect((variant) => applyVariant(container, variant));
    instances.set(container, selector);
    container.variantSelector = selector;
    return selector;
  }

  function containersIn(root) {
    if (root.matches?.(".variant-selector-container")) return [root];
    return Array.from(root.querySelectorAll?.(".variant-selector-container") || []);
  }

  function initAll(root = document) {
    containersIn(root).forEach(initContainer);
  }

  function disposeAll(root) {
    containersIn(root).forEach((container) => instances.get(container)?.dispose());
  }

  document.addEventListener("DOMContentLoaded", () => initAll());
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => disposeAll(event.target));
  window.createVariantSelector = (container, options = {}) => {
    const selector = initContainer(container);
    if (options.colors) selector.updateVariantColors(options.colors);
    if (options.onSelect) selector.onVariantSelect(options.onSelect);
    return selector;
  };
  window.OlvaxinVariantSelector = { lifecycleVersion: 1, init: initAll, dispose: disposeAll };
})();
